import React, { useState, useEffect } from 'react';
import { tcmfApi } from './api';
import { FileUploadForm } from './FileUploadForm';
import { ResultDisplay } from './ResultDisplay';
import { Toast } from './Toast';
import { Header } from './Header';
import { Footer } from './Footer';

export const TcmfGenerator: React.FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [jsonResult, setJsonResult] = useState<string | null>(null);
  const [markdownResult, setMarkdownResult] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [serviceStatus, setServiceStatus] = useState<'checking' | 'online' | 'offline'>('checking');
  const [showToast, setShowToast] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string>('');

  // Check if the service is alive when the component mounts
  useEffect(() => {
    const checkServiceStatus = async () => {
      const isAlive = await tcmfApi.checkHealth();
      setServiceStatus(isAlive ? 'online' : 'offline');
    };

    checkServiceStatus();
  }, []);

  const handleFileSubmit = async (
      file: File,
      outputType: 'json' | 'markdown',
      options: { pretty?: boolean, title?: string }
  ) => {
    setIsLoading(true);
    setErrorMessage(null);

    try {
      if (outputType === 'json') {
        const result = await tcmfApi.convertToJson(file, options.pretty);
        setJsonResult(result);
        setMarkdownResult(null);
      } else {
        const result = await tcmfApi.convertToMarkdown(file, options.title);
        setMarkdownResult(result);
        setJsonResult(null);
      }
    } catch (error) {
      console.error('Error processing file:', error);
      setErrorMessage('Failed to process file. Please check if the Excel format is correct.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      setIsLoading(true);
      const templateBlob = await tcmfApi.getTemplate();

      // Create a URL for the blob
      const url = URL.createObjectURL(templateBlob);

      // Create a temporary link element and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = 'tcmf-template.xlsx';
      document.body.appendChild(link);
      link.click();

      // Clean up
      URL.revokeObjectURL(url);
      document.body.removeChild(link);

      // Show success toast
      setToastMessage('Template downloaded successfully!');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
    } catch (error) {
      console.error('Error downloading template:', error);
      setErrorMessage('Failed to download template. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
      <>
        <Header />
        <div className="tcmf-content">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold mb-2">TCMF Generator</h1>
            <p className="text-gray-600">
              Generate TCMF messages from Excel spreadsheets
            </p>
            <div className="mt-2">
            <span className="inline-flex items-center">
              Status:
              {serviceStatus === 'checking' && <span className="ml-2 text-yellow-500">Checking...</span>}
              {serviceStatus === 'online' && <span className="ml-2 text-green-500">Online</span>}
              {serviceStatus === 'offline' && <span className="ml-2 text-red-500">Offline</span>}
            </span>
            </div>
          </div>

          <div className="mx-auto max-w-3xl px-4">
            <div className="mb-8">
              <FileUploadForm
                  onSubmit={handleFileSubmit}
                  isLoading={isLoading}
              />
            </div>

            <div id="template-section" className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-8">
              <p className="mb-4">
                Need a template to create your TCMF scenarios? Download our Excel template to get started.
              </p>
              <button
                  onClick={handleDownloadTemplate}
                  disabled={isLoading || serviceStatus !== 'online'}
                  className="tcmf-button"
              >
                {isLoading ? 'Downloading...' : 'Download Template'}
              </button>
            </div>
          </div>

          {errorMessage && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                {errorMessage}
              </div>
          )}

          {(jsonResult || markdownResult) && (
              <ResultDisplay
                  jsonResult={jsonResult}
                  markdownResult={markdownResult}
              />
          )}

          {showToast && <Toast message={toastMessage} />}
        </div>
        <Footer />
      </>
  );
};